import type { ShopInfo, TwinState } from './types';

/**
 * Canonical seed data for the Bean Counter Cafe Virtual Twin.
 *
 * USE EXACTLY — the admin app seeds from the same canonical data so the two
 * surfaces stay coherent. If you change a price or an item here, change it in the
 * admin seed too. This is the single source of truth the splash page reads from
 * (it never hardcodes marketing copy or prices in components).
 */

export const TWIN_STATE_VERSION = 1;
export const TWIN_STORAGE_KEY = 'bean-counter:twin:v1';

/** Static shop info (hours/location). Same source the chatbot uses for "are you open?". */
export const SHOP_INFO: ShopInfo = {
  name: 'Bean Counter Cafe',
  tagline: 'Coffee, brewed fresh — counted to the last bean.',
  address_line: '123 Bean St',
  neighborhood: 'Roastery District',
  city: 'Portland',
  region: 'OR',
  postal_code: '97201',
  country: 'US',
  phone: '+1-503-555-0142',
  // Open 7am–6pm daily.
  hours: {
    0: { open: '07:00', close: '18:00' },
    1: { open: '07:00', close: '18:00' },
    2: { open: '07:00', close: '18:00' },
    3: { open: '07:00', close: '18:00' },
    4: { open: '07:00', close: '18:00' },
    5: { open: '07:00', close: '18:00' },
    6: { open: '07:00', close: '18:00' },
  },
  geo: { latitude: 45.5152, longitude: -122.6784 },
  price_range: '$',
};

export function buildSeedState(): TwinState {
  return {
    version: TWIN_STATE_VERSION,
    categories: [
      { id: 'cat-espresso', name: 'Espresso', sort_order: 1, is_active: true },
      { id: 'cat-brewed', name: 'Brewed', sort_order: 2, is_active: true },
      { id: 'cat-pastries', name: 'Pastries', sort_order: 3, is_active: true },
    ],
    menu_items: [
      {
        id: 'item-latte',
        category_id: 'cat-espresso',
        name: 'Latte',
        description: 'Espresso and steamed milk.',
        price_cents: 450,
        image_url: null,
        is_available: true,
        is_active: true,
        is_featured: true,
      },
      {
        id: 'item-cappuccino',
        category_id: 'cat-espresso',
        name: 'Cappuccino',
        description: 'Espresso, steamed milk, and foam.',
        price_cents: 425,
        image_url: null,
        is_available: true,
        is_active: true,
        is_featured: false,
      },
      {
        id: 'item-cold-brew',
        category_id: 'cat-brewed',
        name: 'Cold Brew',
        description: 'Slow-steeped, smooth and bold.',
        price_cents: 400,
        image_url: null,
        is_available: true,
        is_active: true,
        is_featured: true,
      },
      {
        id: 'item-drip',
        category_id: 'cat-brewed',
        name: 'Drip Coffee',
        description: 'House roast, always fresh.',
        price_cents: 275,
        image_url: null,
        is_available: true,
        is_active: true,
        is_featured: false,
      },
      {
        id: 'item-croissant',
        category_id: 'cat-pastries',
        name: 'Croissant',
        description: 'Buttery and flaky.',
        price_cents: 350,
        image_url: null,
        is_available: true,
        is_active: true,
        is_featured: false,
      },
      {
        id: 'item-blueberry-muffin',
        category_id: 'cat-pastries',
        name: 'Blueberry Muffin',
        description: 'Bursting with blueberries.',
        price_cents: 325,
        image_url: null,
        is_available: true,
        is_active: true,
        is_featured: false,
      },
    ],
    menu_item_options: [
      // Latte
      { id: 'opt-latte-size-sm', menu_item_id: 'item-latte', group: 'Size', label: 'Small', price_delta_cents: 0 },
      { id: 'opt-latte-size-lg', menu_item_id: 'item-latte', group: 'Size', label: 'Large', price_delta_cents: 75 },
      { id: 'opt-latte-milk-whole', menu_item_id: 'item-latte', group: 'Milk', label: 'Whole', price_delta_cents: 0 },
      { id: 'opt-latte-milk-oat', menu_item_id: 'item-latte', group: 'Milk', label: 'Oat', price_delta_cents: 60 },
      { id: 'opt-latte-esp-reg', menu_item_id: 'item-latte', group: 'Espresso', label: 'Regular', price_delta_cents: 0 },
      { id: 'opt-latte-esp-decaf', menu_item_id: 'item-latte', group: 'Espresso', label: 'Decaf', price_delta_cents: 0 },
      // Cold Brew
      { id: 'opt-coldbrew-size-reg', menu_item_id: 'item-cold-brew', group: 'Size', label: 'Regular', price_delta_cents: 0 },
      { id: 'opt-coldbrew-size-lg', menu_item_id: 'item-cold-brew', group: 'Size', label: 'Large', price_delta_cents: 100 },
    ],
    customers: [],
    orders: [],
    order_items: [],
    inventory_items: [
      { id: 'inv-beans', name: 'Espresso beans', unit: 'g', quantity_on_hand: 8000, reorder_threshold: 2000 },
      { id: 'inv-whole-milk', name: 'Whole milk', unit: 'ml', quantity_on_hand: 12000, reorder_threshold: 3000 },
      { id: 'inv-oat-milk', name: 'Oat milk', unit: 'ml', quantity_on_hand: 6000, reorder_threshold: 2000 },
      { id: 'inv-cups-12oz', name: '12oz cups', unit: 'each', quantity_on_hand: 400, reorder_threshold: 100 },
    ],
    staff: [
      { id: 'staff-mara', name: 'Mara Okafor', role: 'manager', auth_user_id: null },
      { id: 'staff-deshawn', name: 'DeShawn Lee', role: 'barista', auth_user_id: null },
    ],
  };
}
